"use client"

import type { FC, ReactNode, SVGProps } from "react"
import React, { useMemo, useState } from "react"
import { AnimatePresence, motion } from "motion/react"
import {
	IconCheckOutline18 as Check,
	IconChevronDownOutline18 as ChevronDown,
	IconMagnifierOutline18 as Search,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import { cn } from "@productlane/lib/src/browser"

import {
	InternalPopover,
	InternalPopoverContent,
	InternalPopoverPortal,
	InternalPopoverTrigger,
} from "../../internal/popover"
import {
	InternalSelect,
	InternalSelectContent,
	InternalSelectTrigger,
	InternalSelectValue,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectSeparator,
} from "../../internal/select"
import { Label } from "../label"

export const OptionSchema = z.object({
	label: z.custom<ReactNode>(),
	value: z.string().min(1),
	triggerLabel: z.custom<ReactNode>().optional(),
	separatorBefore: z.boolean().optional(),
	/** Small heading rendered above this option, starting a group. */
	groupLabel: z.custom<ReactNode>().optional(),
})
export type Option = z.infer<typeof OptionSchema>

export interface SelectProps {
	value?: string | undefined
	onChange?: (value: string) => void
	className?: string
	selectClassName?: string
	iconClassName?: string
	chevronClassName?: string
	options: Array<Option>
	label?: ReactNode
	disabled?: boolean
	description?: ReactNode
	name: string
	errorMessage?: ReactNode
	showErrorMessage?: boolean
	placeholder?: string
	Icon?: LucideIcon
	searchable?: boolean
	searchPlaceholder?: string
	/** Searchable only: the caller filters `options` itself, e.g. with a query that searches the server. */
	onSearchChange?: (search: string) => void
	onOpenChange?: (open: boolean) => void
	popoverClassName?: string
	popoverAlign?: "start" | "center" | "end"
	/** Searchable only: options toggle on and off and the menu stays open. */
	multiple?: boolean
	selectedValues?: readonly string[]
	onToggle?: (value: string) => void
	/** What the trigger shows instead of the selected option's label. */
	displayValue?: ReactNode
}

const GROUP_LABEL_CLASS =
	"px-2 pb-1 pt-2 text-xs font-medium text-tertiary dark:text-tertiary"

const SearchableSelect = React.memo(
	React.forwardRef<HTMLButtonElement, SelectProps>(
		(
			{
				value,
				onChange,
				className,
				selectClassName,
				iconClassName,
				chevronClassName,
				options,
				disabled,
				name,
				errorMessage,
				label,
				description,
				showErrorMessage = true,
				Icon,
				placeholder = "Select...",
				searchPlaceholder = "Search...",
				onSearchChange,
				onOpenChange,
				popoverClassName,
				popoverAlign = "start",
				multiple = false,
				selectedValues = [],
				onToggle,
				displayValue,
			},
			ref,
		) => {
			const [open, setOpen] = useState(false)
			const [search, setSearch] = useState("")
			const isSelected = (optionValue: string) =>
				multiple ? selectedValues.includes(optionValue) : value === optionValue
			const updateSearch = (next: string) => {
				setSearch(next)
				onSearchChange?.(next)
			}
			const handleOpenChange = (next: boolean) => {
				setOpen(next)
				if (!next) updateSearch("")
				onOpenChange?.(next)
			}

			const filtersItself = !!onSearchChange
			const filteredOptions = useMemo(() => {
				if (!search || filtersItself) return options
				const lowerSearch = search.toLowerCase()
				return options.filter((option) => {
					const labelText =
						typeof option.label === "string" ? option.label : option.value
					return labelText.toLowerCase().includes(lowerSearch)
				})
			}, [options, search, filtersItself])

			const selectedOption = options.find((opt) => opt.value === value)

			return (
				<div className={cn("flex flex-col", className)}>
					{label && (
						<Label className="mb-1.5" htmlFor={name}>
							{label}
						</Label>
					)}
					<InternalPopover open={open} onOpenChange={handleOpenChange}>
						<InternalPopoverTrigger asChild disabled={disabled}>
							<button
								ref={ref}
								name={name}
								type="button"
								className={cn(
									"flex h-8 w-full items-center gap-2 rounded-md bg-white pl-3 pr-2 text-sm shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed dark:bg-gray-900 dark:text-gray-50",
									errorMessage
										? "ring-red-300 dark:ring-red-700"
										: "ring-black/15 dark:ring-white/15",
									selectClassName,
								)}
							>
								{Icon && <Icon className={cn("h-4 w-4", iconClassName)} />}
								<span className="flex-1 truncate text-left">
									{displayValue ??
										selectedOption?.triggerLabel ??
										selectedOption?.label ??
										placeholder}
								</span>
								<ChevronDown
									className={cn("h-3 w-3 opacity-50", chevronClassName)}
									strokeWidth={1.75}
								/>
							</button>
						</InternalPopoverTrigger>
						<InternalPopoverPortal>
							<InternalPopoverContent
								className={cn(
									"w-[var(--radix-popover-trigger-width)] p-0",
									popoverClassName,
								)}
								align={popoverAlign}
							>
								<div className="flex items-center border-b border-gray-200 px-3 dark:border-gray-700/80">
									<Search className="h-4 w-4 shrink-0 opacity-50" />
									<input
										className="flex h-9 w-full bg-transparent px-2 py-2 text-sm outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-50"
										placeholder={searchPlaceholder}
										value={search}
										onChange={(e) => updateSearch(e.target.value)}
									/>
								</div>
								<div className="max-h-52 overflow-auto p-1">
									{filteredOptions.length === 0 ? (
										<div className="py-6 text-center text-sm text-tertiary">
											No results found.
										</div>
									) : (
										filteredOptions.map((option, index) => (
											<React.Fragment key={option.value}>
												{option.separatorBefore && index > 0 && (
													<div className="my-1 h-px bg-gray-100 dark:bg-gray-700" />
												)}
												{option.groupLabel && (
													<div className={GROUP_LABEL_CLASS}>
														{option.groupLabel}
													</div>
												)}
												<button
													type="button"
													className={cn(
														"relative flex w-full cursor-default select-none items-center rounded-md py-1.5 pl-2 pr-8 text-sm font-medium outline-none hover:bg-gray-100 dark:hover:bg-gray-800",
														!multiple &&
															isSelected(option.value) &&
															"bg-gray-100 dark:bg-gray-800",
													)}
													onClick={() => {
														if (multiple) {
															onToggle?.(option.value)
															return
														}
														onChange?.(option.value)
														handleOpenChange(false)
													}}
												>
													<span className="min-w-0 flex-1 truncate text-left">
														{option.label}
													</span>
													{isSelected(option.value) && (
														<span className="absolute right-2 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
															<Check className="h-4 w-4" />
														</span>
													)}
												</button>
											</React.Fragment>
										))
									)}
								</div>
							</InternalPopoverContent>
						</InternalPopoverPortal>
					</InternalPopover>
					<AnimatePresence>
						{errorMessage && showErrorMessage && (
							<motion.p
								initial={{
									opacity: 0,
									height: 0,
									marginTop: 0,
								}}
								animate={{
									opacity: 1,
									height: "auto",
									marginTop: "0.5rem",
								}}
								exit={{
									opacity: 0,
									height: 0,
									marginTop: 0,
								}}
								transition={{ duration: 0.1 }}
								className="mt-1.5 text-sm text-red-600"
								id={`${name}-error`}
							>
								{errorMessage}
							</motion.p>
						)}
					</AnimatePresence>
					{description && (
						<div className="mt-2 text-sm text-tertiary">{description}</div>
					)}
				</div>
			)
		},
	),
)

const StandardSelect = React.memo(
	React.forwardRef<HTMLButtonElement, SelectProps>(
		(
			{
				value,
				onChange,
				className,
				selectClassName,
				iconClassName,
				chevronClassName,
				options,
				disabled,
				name,
				errorMessage,
				label,
				description,
				showErrorMessage = true,
				Icon,
				placeholder = "Select...",
				popoverAlign = "start",
			},
			ref,
		) => {
			return (
				<div className={cn("flex flex-col", className)}>
					{label && (
						<Label className="mb-1.5" htmlFor={name}>
							{label}
						</Label>
					)}
					<InternalSelect
						disabled={disabled}
						value={value}
						onValueChange={onChange}
					>
						<InternalSelectTrigger
							className={cn(selectClassName)}
							name={name}
							isError={!!errorMessage}
							Icon={Icon}
							iconClassName={iconClassName}
							chevronClassName={chevronClassName}
							ref={ref}
						>
							<InternalSelectValue placeholder={placeholder} />
						</InternalSelectTrigger>
						<InternalSelectContent align={popoverAlign}>
							<SelectGroup className="max-h-64 overflow-y-auto overflow-x-hidden">
								{options.map((option, index) => (
									<React.Fragment key={option.value}>
										{option.separatorBefore && index > 0 && <SelectSeparator />}
										{option.groupLabel && (
											<SelectLabel className={GROUP_LABEL_CLASS}>
												{option.groupLabel}
											</SelectLabel>
										)}
										<SelectItem value={option.value}>{option.label}</SelectItem>
									</React.Fragment>
								))}
							</SelectGroup>
						</InternalSelectContent>
					</InternalSelect>
					<AnimatePresence>
						{errorMessage && showErrorMessage && (
							<motion.p
								initial={{
									opacity: 0,
									height: 0,
									marginTop: 0,
								}}
								animate={{
									opacity: 1,
									height: "auto",
									marginTop: "0.5rem",
								}}
								exit={{
									opacity: 0,
									height: 0,
									marginTop: 0,
								}}
								transition={{ duration: 0.1 }}
								className="mt-1.5 text-sm text-red-600"
								id={`${name}-error`}
							>
								{errorMessage}
							</motion.p>
						)}
					</AnimatePresence>
					{description && (
						<div className="mt-2 text-sm text-tertiary">{description}</div>
					)}
				</div>
			)
		},
	),
)

export const Select = React.memo(
	React.forwardRef<HTMLButtonElement, SelectProps>((props, ref) => {
		if (props.searchable) {
			return <SearchableSelect {...props} ref={ref} />
		}
		return <StandardSelect {...props} ref={ref} />
	}),
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
